<template>
  <v-container>
    <v-responsive class="mx-auto">
      <v-breadcrumbs :items="breadcrumbs" />

      <v-alert
        type="warning"
        v-if="warnings.length"
        outlined
      >
        <p class="font-weight-bold"
          v-show="registration.status !== RegistrationStatus.COMPLETED"
        >
          Please rectify the following issues before finalising this registration.
        </p>
        <ul>
          <li
            v-for="warning of warnings"
            :key="warning"
          >
            {{ warning }}
          </li>
        </ul>
      </v-alert>

      <div class="text-h3">
        {{ registration.memberName }}
      </div>

      <v-tabs
        v-model="tab"
        class="my-2"
      >
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="membership">Membership</v-tab>
        <v-tab key="application">Application</v-tab>
        <v-tab key="details">Contact Details</v-tab>
        <v-tab key="persons">People</v-tab>
        <v-tab key="activities">Business Activities and Programs</v-tab>
        <v-tab key="brands">Brands</v-tab>
        <v-tab key="reports">Reports</v-tab>
        <v-tab key="tools">Tools</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item key="membership">
          <v-divider />
          <v-subheader>Other Sources</v-subheader>
          <v-btn
            color="info"
            outlined
            small
            :disabled="!registration.abn"
            @click="$router.push({ name: 'AdminAusMeatCompanyList', params: { abn: registration.abn } })"
          >
            Search AUS-MEAT Database
          </v-btn>
          <registration-membership-details
            :registrationId="id"
            v-bubble.error
            v-bubble.info
            @change="$apollo.queries.registration.refresh()"
          />
        </v-tab-item>

        <v-tab-item key="application">
          <registration-application-details
            :registrationId="id"
            v-bubble.error
            v-bubble.info
          />
        </v-tab-item>

        <v-tab-item key="details">
          <p class="font-weight-bold">Addresses</p>
          <registration-addresses
            :registration-id="id"
            v-bubble.error
            v-bubble.info
            :requiredAddressTypes="[AddressType.POSTAL, AddressType.BUSINESS]"
            @change="$apollo.queries.registration.refresh()"
          />

          <p class="font-weight-bold mt-5">Company contact information</p>
          <registration-contact-details
            :registration-id="id"
            v-bubble.error
            v-bubble.info
            :requiredContactDetailTypes="[ContactDetailType.EMAIL]"
            @change="$apollo.queries.registration.refresh()"
          />
        </v-tab-item>

        <v-tab-item key="persons">
          <v-divider class="my-2" />
          <registration-persons
            :registration-id="id"
            v-bubble.error
            v-bubble.info
            @change="$apollo.queries.registration.refresh()"
          />
        </v-tab-item>

        <v-tab-item key="activities">
          <registration-activities-table
            :registrationId="id"
            v-bubble.error
            v-bubble.info
            @change="$apollo.queries.registration.refresh()"
          />
        </v-tab-item>

        <v-tab-item key="brands">
          <registration-brands
            :registrationId="id"
            v-bubble.error
            v-bubble.info
            @change="$apollo.queries.registration.refresh()"
          />
        </v-tab-item>

        <v-tab-item key="reports">
          <router-link :to="{ name: 'MemberTraineesReport', query: { memberNumber: registration.memberNumber } }">
            MSA Training Status Report
          </router-link>
        </v-tab-item>

        <v-tab-item key="tools">
          <v-btn
            color="warning"
            outlined
            :loading="publishing"
            :disabled="disableInput"
            @click="publish"
          >
            Force publish member data
          </v-btn>
          <v-btn
            color="warning"
            outlined
            :loading="publishing"
            :disabled="disableInput"
            @click="sendAccessCode"
          >
            Re-send access code
          </v-btn>
        </v-tab-item>
      </v-tabs-items>

      <v-row class="mt-5">
        <v-col cols="12">
          <history-dialog
            model="Registration"
            :_id="id"
            v-bubble.error
            v-bubble.info
          />
        </v-col>
      </v-row>
    </v-responsive>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { compose, intersection, length, not, uniq } from 'ramda'
import { SourceOfTruth, RegistrationStatus, AddressType, ContactDetailType } from '@mymsa/common'

import HistoryDialog from '../components/HistoryDialog.vue'
import RegistrationActivitiesTable from '../components/RegistrationActivitiesTable.vue'
import abrMixin from '../mixins/abr-mixin'
import RegistrationMembershipDetails from '../components/RegistrationMembershipDetails.vue'
import RegistrationAddresses from '../components/RegistrationAddresses.vue'
import RegistrationContactDetails from '../components/RegistrationContactDetails.vue'
import RegistrationApplicationDetails from '../components/RegistrationApplicationDetails.vue'
import RegistrationPersons from '../components/RegistrationPersons.vue'
import RegistrationBrands from '../components/RegistrationBrands.vue'
import { generateAccessCode } from '../utilities'

const ni = compose(not, length, intersection)

export default {
  name: 'AdminRegistrationView',

  props: {
    id: {
      type: String,
      required: true
    }
  },

  components: {
    HistoryDialog,
    RegistrationActivitiesTable,
    RegistrationMembershipDetails,
    RegistrationAddresses,
    RegistrationContactDetails,
    RegistrationApplicationDetails,
    RegistrationPersons,
    RegistrationBrands
  },

  mixins: [
    abrMixin
  ],

  data () {
    return {
      RegistrationStatus,
      AddressType,
      ContactDetailType,
      tab: null,
      publishing: false,
      disableInput: false,
      registration: {
        contactDetails: [],
        activities: [],
        addresses: [],
        persons: []
      },
      breadcrumbs: [
        { text: 'Admin', exact: true, to: { name: 'Admin' } },
        { text: this.$route.meta?.title || this.$route.name }
      ]
    }
  },

  apollo: {
    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          abn
          status
          sourceOfTruth
          memberNumber
          memberName
          status
          createdAt
          updatedAt
          activities {
            _id
            from
            to
            memberType {
              _id
              value
              trainingRequired
              siteVisitRequired
            }
            trainingComplete
            siteVisitComplete
          }
          contactDetails {
            _id
            type
          }
          addresses {
            _id
            type
          }
          persons {
            _id
            end
            isActive
            isPrimaryContact
            person {
              _id
              email
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.id
        }
      },
      error (e) { this.$emit('error', e) }
    }
  },

  computed: {
    warnings () {
      const { registration } = this
      const warnings = []
      const now = new Date()
      if (!registration) return warnings
      const addressTypes = registration.addresses.map(o => o.type)
      const contactDetailTypes = registration.contactDetails.map(o => o.type)

      if (registration.status === RegistrationStatus.INITIAL) {
        warnings.push('This registration has not been submitted yet.')
        return warnings // no need to display additional warnings
      }

      registration.sourceOfTruth === SourceOfTruth.LPA &&
        warnings.push('The source of truth for this registration is LPA. Details set here may be overwritten by system processes.')

      !registration.contactDetails.length &&
        warnings.push('This registration has no business contact details.')

      ni(contactDetailTypes, [ContactDetailType.EMAIL]) &&
        warnings.push('This registration does not have a business email address configured.')

      !registration.activities.filter(o => !o.to || (new Date(o.to) > now)).length &&
        warnings.push('This registration has no current activities set.')

      !registration.persons.filter(o => o.isActive).length &&
        warnings.push('This registration has no active people attached.')

      ni(addressTypes, [AddressType.POSTAL]) &&
        warnings.push('This registration does not have a postal address configured.')

      ni(addressTypes, [AddressType.BUSINESS]) &&
        warnings.push('This registration does not have a business address configured.')

      const trainingRequired = registration.activities
        .some(o => o.memberType && o.memberType.trainingRequired && !o.trainingComplete)
      trainingRequired &&
        warnings.push('Face to face training is required for this registration.')

      const siteVisitRequired = registration.activities
        .some(o => o.memberType && o.memberType.siteVisitRequired && !o.siteVisitComplete)
      siteVisitRequired &&
        warnings.push('A site visit is required for this registration.')

      return uniq(warnings)
    },

    primaryContact () {
      return this.registration.persons.find(o => o.isPrimaryContact)
    }
  },

  methods: {
    async publish () {
      this.disableInput = true
      this.publishing = true
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!) {
            registrationForcePublishChanges(_id: $id)
          }`,
          variables: {
            id: this.id
          }
        })
      } catch (e) {
        this.$emit('error', e)
      } finally {
        this.publishing = false
        this.disableInput = false
      }
    },

    async sendAccessCode () {
      this.disableInput = true
      const email = this.primaryContact?.person?.email
      const accessCode = generateAccessCode()
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $email: String!, $accessCode: String!) {
            registrationSendNewAccessCode(_id: $id, email: $email, accessCode: $accessCode)
          }`,
          variables: {
            id: this.id,
            email,
            accessCode
          }
        })
      } catch (e) {
        this.$emit('error', e)
        return
      } finally {
        this.disableInput = false
      }
      this.$emit('info', `The access code "${accessCode}" was sent to "${email}".`)
    }
  }
}
</script>
